import PrimaryButton from "@components/PrimaryButton/PrimaryButton"
import TransactionDetailHeader from "@components/TransactionDetailHeader/TransactionDetailHeader"
import TransactionInformationRow from "@components/TransactionInformationRow/TransactionInformationRow"
import {
    Container,
    ContentContainer,
    DetailLabel,
    DetailBox,
    ButtonContainer,
    RedemptionCard,
    RedemptionStatusRow,
    RedemptionStatusText,
    Divider,
    RedemptionDetailsRow,
    RedemptionDetailsText,
    FieldRowsContainer,
    ButtonIconContainer,
    ButtonContentWrapper,
    ButtonGroupContainer,
    HorizontalButtonGroup,
    AlertContainer
} from "./TransactionDetail.styles"
import { useNavigate, useLocation } from "react-router"
import SkeletonLoader from "@components/SkeletonLoader/SkeletonLoader"
import React, { useState } from "react"
import HelpSection from "@components/HelpSection/HelpSection"
import CircularProgress from "@components/CircularProgress/CircularProgress"
import { BLACK, PRIMARY, VIOLET_400 } from "@styles/colors"
import Chevron from "@components/Chevron/Chevron"
import MultipartRedemptionBottomSheet from "@components/MultipartRedemptionBottomSheet/MultipartRedemptionBottomSheet"
import { ModalWithIcon } from "@components/ModalWithIcon/ModalWithIcon"
import { useTranslation } from "react-i18next"
import { formatDate, redirectToTelegramSupport, toCurrencyFormat } from "@lib/utils"
import { getPlatform } from "@lib/telegram"
import useActivityDetail from "@hooks/useActivityDetail"
import ShareIcon from "@components/ShareIcon/ShareIcon"
import { DETAILED_ACTIVITIES } from "@constants/transaction"
import useBankManagement from "@hooks/useManageBank"
import useEWalletManagement from "@hooks/useManageEWallet"
import useManagePaymentBank from "@hooks/useManagePaymentBank"
import usePaymentLinkActivityDetail from "@hooks/usePaymentLinkActivityDetail"
import useManagePaymentLink from "@hooks/useManagePaymentLink"
import LoadingModal from "@components/LoadingModal/LoadingModal"
import { usePDFShare } from "@hooks/usePDFShare"
import CycleIcon from "@components/CycleIcon/CycleIcon"
import AlertBox from "@components/AlertBox/AlertBox"

const TransactionDetail = () => {
    const navigate = useNavigate()
    const location = useLocation()
    const { t } = useTranslation()
    const [isOpen, setIsOpen] = useState(false)
    const [isVoidOpen, setIsVoidOpen] = useState(false)

    const [isButtonPressedLoading, setIsButtonPressedLoading] = useState(false)

    const uuid = location.state?.uuid
    const transaction_uuid = location.state?.transaction_uuid
    const code = location.state?.code
    const status = location.state?.status
    const ui_amount = location.state?.ui_amount
    const from = location.state?.from

    const { voidPaymentLink } = useManagePaymentLink()

    const {
        data: paymentLinkActivityDetail,
        isLoading: paymentLinkActivityDetailLoading
    } = usePaymentLinkActivityDetail({
        uuid,
        fetchImmediately: from === '/payment-link'
    })

    const {
        data: activityDetail,
        isLoading: activityDetailLoading
    } = useActivityDetail({
        uuid,
        transaction_uuid,
        code,
        fetchImmediately: from === '/home' || from === undefined
    })

    const isLoading = paymentLinkActivityDetailLoading || activityDetailLoading
    const data = paymentLinkActivityDetail || activityDetail

    // PDF sharing hook
    const { shareOrDownloadPDF, isShareLoading } = usePDFShare(
        data ? {
            transactionId: data.uuid,
            transactionDate: data.timestamp,
            amount: data.ui_amount,
            bank: data.bank,
            accountNumber: data.account,
            accountName: data.account_name,
            status: data.status,
            fees: (Number(data.fee || 0)).toString(),
            totalAmount: (Number(data.ui_amount || 0) + Number(data.fee || 0)).toString()
        } : null,
        isLoading
    )

    const { banks } = useBankManagement(Number(code) === 201)
    const { ewallets } = useEWalletManagement(Number(code) === 203)
    const { data: managePaymentBankData, supportedBanks } = useManagePaymentBank(Number(code) === 301)

    const handleRetryTransaction = async () => {
        switch (Number(code)) {
            case 201:
                const bank = banks.find(bank => bank.bank_name === data?.bank && bank.bank_account === data?.account)
                return navigate('/redeem-bank', {
                    state: {
                        bank_uuid: bank?.bank_uuid,
                        amount: ui_amount
                    }
                })
            case 203:
                const ewallet = ewallets.find(ewallet => ewallet.provider === data?.provider && ewallet.phone_number === data?.phone_number)
                return navigate('/redeem-ewallet', {
                    state: {
                        ewallet_uuid: ewallet?.uuid,
                        amount: ui_amount
                    }
                })
            case 301:
                const paymentBank = managePaymentBankData?.find(paymentBank => paymentBank.bank_name === data?.bank && paymentBank.bank_account === data?.account)
                const selectedBank = supportedBanks?.find(bank => bank.code === paymentBank?.bank_name)
                const paymentInformation = JSON.stringify({
                    paymentDestination: {
                        selectedBank: selectedBank,
                        accountNumber: paymentBank?.bank_account,
                        accountHolderName: paymentBank?.bank_account_name,
                        accountTag: paymentBank?.account_nickname,
                    },
                    amount: ui_amount,
                })
                return navigate('/bank-transfer-payment', {
                    state: {
                        paymentInformation
                    }
                })
        }
    }

    const handleVoidTransaction = () => {
        setIsVoidOpen(true)
    }

    const handleConfirmVoidTransaction = async () => {
        await voidPaymentLink(uuid)
        handleGoBack()
    }

    const handleGoBack = () => {
        // Check if we have state with a 'from' property
        if (location.state && location.state.from) {
            navigate(location.state.from)
        } else {
            // Default fallback to home if no previous location is specified
            navigate('/home')
        }
    }

    const handleShareAndDownload = async () => {
        setIsButtonPressedLoading(true)
        try {
            await shareOrDownloadPDF()
        } finally {
            setIsButtonPressedLoading(false)
        }
    }

    let FieldRows: React.ReactNode
    if (data) {
        FieldRows = <FieldRowsContainer>
            {Object.keys(data).map((key) => {
                // ignore empty values
                if (!data[key]) return

                // ignore key
                if (key === 'buyer_email') return
                if (key === 'additionalDetail') return

                if (key === 'status') {
                    return <TransactionInformationRow
                        key={key}
                        type="status"
                        title={DETAILED_ACTIVITIES[key]}
                        value={data[key]}
                    />
                }

                if (key === 'payment_link_status') {
                    return <TransactionInformationRow
                        key={key}
                        type="payment-link-status"
                        title={DETAILED_ACTIVITIES[key]}
                        value={data[key]}
                    />
                }

                if (
                    key === 'from' ||
                    key === 'to' ||
                    key === 'blockchain_tx_id'
                ) {
                    return <TransactionInformationRow
                        key={key}
                        type="normal"
                        title={DETAILED_ACTIVITIES[key]}
                        value={data[key]}
                        onCopy={() => { }}
                    />
                }

                if (key === 'sn') {
                    return <TransactionInformationRow
                        key={key}
                        type="normal"
                        title={DETAILED_ACTIVITIES[key]}
                        value={data[key]}
                        onCopy={() => { }}
                    />
                }

                if (key === 'token') {
                    return <TransactionInformationRow
                        key={key}
                        type="bold"
                        title={DETAILED_ACTIVITIES[key]}
                        value={data[key]}
                        onCopy={() => { }}
                    />
                }

                if (key === 'payment_link') {
                    return <TransactionInformationRow
                        key={key}
                        type="normal"
                        title={DETAILED_ACTIVITIES[key]}
                        value={data[key]}
                        onCopy={() => { }}
                        qrHeaderTitle={t('components.paymentLinkAmountCard.qrCodeTitle')}
                        qrLabel={t('components.paymentLinkAmountCard.shareToPayerLabel')}
                    />
                }

                if (
                    key === 'ex_rate' ||
                    key === 'fee' ||
                    key === 'merchant_fee' ||
                    key === 'merchant_full_amount' ||
                    key === 'network_fee'
                ) {
                    return <TransactionInformationRow
                        key={key}
                        type="normal"
                        title={DETAILED_ACTIVITIES[key]}
                        value={toCurrencyFormat(data[key])}
                    />
                }

                if (key === 'selling_price') {
                    return <TransactionInformationRow
                        key={key}
                        type="normal"
                        title={DETAILED_ACTIVITIES[key]}
                        value={`Rp${toCurrencyFormat(data[key])}`}
                    />
                }

                if (
                    key === 'ui_amount' ||
                    key === 'total_amount' ||
                    key === 'jidr_amount' ||
                    key === 'top_up_amount'
                ) {
                    let unit = 'JIDR'
                    let type: 'bold' | 'normal' | 'status' | 'payment-link-status' = 'bold'

                    if (
                        Number(code) === 301 ||
                        Number(code) === 201 ||
                        Number(code) === 101 ||
                        code === 'paymentlink'
                    ) {
                        unit = 'IDR'
                        // if (key === 'ui_amount' || key === 'top_up_amount') {
                        //     type = 'normal'
                        // }
                    }

                    return <TransactionInformationRow
                        key={key}
                        type={type}
                        title={DETAILED_ACTIVITIES[key]}
                        value={`${toCurrencyFormat(data[key])} ${unit}`}
                    />
                }

                if (
                    key === 'approval_date' ||
                    key === 'transfer_date' ||
                    key === 'timestamp' ||
                    key === 'expiry_time' ||
                    key === 'date'
                ) {
                    return <TransactionInformationRow
                        key={key}
                        type="normal"
                        title={DETAILED_ACTIVITIES[key]}
                        value={formatDate(data[key], true)}
                    />
                }

                return <TransactionInformationRow
                    key={key}
                    type="normal"
                    title={DETAILED_ACTIVITIES[key]}
                    value={data[key]}
                />
            })}
        </FieldRowsContainer>
    }

    let Buttons: React.ReactNode = null
    // For payment link
    if (!code) {
        if (status === 'OPEN' || paymentLinkActivityDetail?.status === 'OPEN') {
            Buttons =
                <PrimaryButton
                    onClick={() => handleVoidTransaction()}
                    variant="outline"
                >
                    {t('transactionDetail.buttons.voidTransaction')}
                </PrimaryButton>
        }
    } else if ((Number(code || 0) === 201 || Number(code || 0) === 203) && (status === 'COMPLETED' || activityDetail?.status === 'COMPLETED')) {
        // RTB and RTE
        const RedeemRepeat = (
            <ButtonContentWrapper>
                <ButtonIconContainer>
                    <CycleIcon width={16} height={16} />
                </ButtonIconContainer>
                <span>
                    {t('transactionDetail.buttons.repeatThisRedeem')}
                </span>
            </ButtonContentWrapper>
        )
        Buttons =
            <ButtonGroupContainer>
                <PrimaryButton
                    variant="outline"
                    onClick={handleRetryTransaction}
                    children={RedeemRepeat}
                />
                <PrimaryButton
                    onClick={handleGoBack}
                >
                    {t('transactionDetail.buttons.backToHome')}
                </PrimaryButton>
            </ButtonGroupContainer>
    } else if (Number(code || 0) === 301 && (status === 'COMPLETED' || activityDetail?.status === 'COMPLETED')) {
        // Bank Payment
        const platform = getPlatform()
        const PaymentRepeat = (
            <ButtonContentWrapper>
                <ButtonIconContainer>
                    <CycleIcon width={16} height={16} />
                </ButtonIconContainer>
                <span>
                    {t('transactionDetail.buttons.repeatThisTransaction')}
                </span>
            </ButtonContentWrapper>
        )
        const Share = (
            <ButtonContentWrapper>
                <ButtonIconContainer>
                    <ShareIcon type={2} fill={PRIMARY} />
                </ButtonIconContainer>
                <span>
                    {t('transactionDetail.buttons.share')}
                </span>
            </ButtonContentWrapper>
        )
        Buttons =
            <ButtonGroupContainer>
                <PrimaryButton
                    variant="outline"
                    children={PaymentRepeat}
                    onClick={handleRetryTransaction}
                />
                {/* Hide share button on Android - Android WebView doesn't support Web Share API */}
                {platform !== 'android' && (
                    <HorizontalButtonGroup>
                        <PrimaryButton
                            variant="outline"
                            onClick={handleShareAndDownload}
                            children={Share}
                        />
                    </HorizontalButtonGroup>
                )}
            </ButtonGroupContainer>
    } else if ((
        Number(code || 0) === 301 ||
        Number(code || 0) === 201 ||
        Number(code || 0) === 203
    ) && (
            status === 'REJECTED' ||
            status === 'FAILED' ||
            activityDetail?.status === 'REJECTED' ||
            activityDetail?.status === 'FAILED'
        )) {
        Buttons =
            <React.Fragment>
                <PrimaryButton
                    onClick={handleRetryTransaction}
                    variant="outline"
                >
                    {t('transactionDetail.buttons.retryTransaction')}
                </PrimaryButton>
            </React.Fragment>
    } else {
        Buttons =
            <PrimaryButton
                onClick={handleGoBack}
            >
                {t('transactionDetail.buttons.backToHome')}
            </PrimaryButton>
    }

    return (
        <Container>
            <LoadingModal visible={isButtonPressedLoading || isShareLoading} />
            <TransactionDetailHeader
                isLoading={isLoading}
                amount={ui_amount || activityDetail?.ui_amount}
                code={code ? Number(code) : undefined}
                goBackTo={location.state?.from} />
            <ContentContainer>
                {data?.additionalDetail &&
                    data?.additionalDetail?.isMultipart &&
                    <RedemptionCard>
                        <RedemptionStatusRow>
                            <CircularProgress
                                maxValue={Number(data.additionalDetail?.totalCount) || 0}
                                value={Number(data.additionalDetail?.disbursedCount) || 0}
                                size={42}
                                strokeWidth={2}
                                fontSize={14}
                                primaryColor={VIOLET_400}
                            />
                            <RedemptionStatusText>{t('transactionDetail.redemption.statusText')}</RedemptionStatusText>
                        </RedemptionStatusRow>
                        <Divider />
                        <RedemptionDetailsRow onClick={() => setIsOpen(true)}>
                            <RedemptionDetailsText>{t('transactionDetail.redemption.detailsText')}</RedemptionDetailsText>
                            <Chevron strokeWidth={1} color="#0088CC" />
                        </RedemptionDetailsRow>
                        <MultipartRedemptionBottomSheet
                            isOpen={isOpen}
                            onClose={() => setIsOpen(false)}
                            height="360px"
                            requested={data.additionalDetail?.requested || '0'}
                            disbursed={data.additionalDetail?.disbursed || '0'}
                            inProgress={data.additionalDetail?.inProgress || '0'}
                            returned={data.additionalDetail?.returned || '0'}
                        />
                    </RedemptionCard>}
                <DetailLabel>{t('transactionDetail.details.label')}</DetailLabel>
                {(status === 'PROCESSING_DISBURSEMENT' || activityDetail?.status === 'PROCESSING_DISBURSEMENT')
                    &&
                    <AlertContainer>
                        <AlertBox
                            alertText={t("transactionDetail.alerts.processingCancelAlert")}
                        />
                    </AlertContainer>
                }
                <DetailBox>
                    {isLoading ? <React.Fragment>
                        <SkeletonLoader width="30%" />
                        <SkeletonLoader />
                        <SkeletonLoader width="30%" />
                        <SkeletonLoader />
                        <SkeletonLoader width="30%" />
                        <SkeletonLoader />
                        <SkeletonLoader width="30%" />
                        <SkeletonLoader />
                        <SkeletonLoader width="30%" />
                        <SkeletonLoader />
                    </React.Fragment>
                        :
                        <React.Fragment>
                            {FieldRows}
                        </React.Fragment>
                    }
                </DetailBox>
                <HelpSection onContactClick={() => redirectToTelegramSupport('JagadCS_bot')} />
            </ContentContainer>
            <ButtonContainer>
                {isLoading ? <SkeletonLoader width="100%" height="48px" /> : Buttons}
            </ButtonContainer>
            <ModalWithIcon
                title={t('transactionDetail.voidModal.title')}
                description={t('transactionDetail.voidModal.description')}
                onPositive={() => handleConfirmVoidTransaction()}
                positiveText={t('transactionDetail.voidModal.confirmButton')}
                visible={isVoidOpen}
                type="void"
                closeButton={true}
                onClose={() => setIsVoidOpen(false)}
                closeIconColor={BLACK}
            />
        </Container>
    )
}

export default TransactionDetail